/**
 * Game mode arena (plan 47): the shared contract of a game ROUND between the
 * backend, the voice client and the apps — and the one pure rule that turns a
 * judged attempt into the stamp the learner sees.
 *
 * The judgment is split (plan 47 § The idea): Jev judges the MEANING of a
 * translate-it attempt (`GameMeaningVerdict`, the host's attempt check), the
 * corrections side-runner judges its FORM (the `correction` chips and the
 * turn's `corrections.done` verdict), and {@link gameStampOf} combines both —
 * reading only the chips that touch the sentence the meaning check judged
 * ({@link gameTurnStampOf}). The live thread, the session history, the
 * end-of-game summary and the eval harness all call the same function, so a
 * stamp and a chip computed from the same data can never contradict each
 * other.
 *
 * Dependency-free on purpose (zod only): the speak client, the backend and the
 * eval harness import it alike.
 */
import { z } from 'zod';
import { GAME_SEEDS } from './english.js';
import type { CorrectionsVerdict } from './ws.js';
import { locateTextSpan, locateTextSpans } from './text-span.js';

/* -------------------------------------------------------------------------- */
/* Meaning verdict, rematch, actions                                          */
/* -------------------------------------------------------------------------- */

/**
 * The meaning verdict of ONE learner turn of translate-it:
 *
 * - `correct`     — says what the challenge says, with no error (Jev).
 * - `minor`       — says it, with a slip a native speaker would notice (Jev).
 * - `wrong`       — does not say it: meaning missing or changed (Jev).
 * - `not-attempt` — not an attempt at the challenge: Jev said so (a question,
 *                   chat), or the code did — the turn stopped mid-clause, or
 *                   no challenge was on the table yet. Never stamped.
 *
 * Same values as `GameAttemptVerdict` of `@orborix/module-sdk`.
 */
export const GAME_MEANING_VERDICTS = ['correct', 'minor', 'wrong', 'not-attempt'] as const;
export const gameMeaningVerdictSchema = z.enum(GAME_MEANING_VERDICTS);
export type GameMeaningVerdict = z.infer<typeof gameMeaningVerdictSchema>;

/**
 * Why the challenge on the table is one the learner already tried:
 *
 * - `retry`       — the learner asked for it (the `retry` game action).
 * - `second-shot` — the coach gave the one instant second shot after a miss.
 */
export const GAME_REMATCH_KINDS = ['retry', 'second-shot'] as const;
export const gameRematchSchema = z.enum(GAME_REMATCH_KINDS);
export type GameRematch = z.infer<typeof gameRematchSchema>;

/**
 * What the learner can ask the game for, outside the conversation. Sent in a
 * `session.update` patch under {@link GAME_ACTION_PATCH_KEY}; the server turns
 * it into a coach turn (an internal cue).
 *
 * - `retry`  — re-serve the challenge of the learner's LAST judged attempt
 *              (translate-it). The card flips back to it; it is not a new
 *              round.
 * - `skip`   — leave the challenge on the table and get a fresh one; the
 *              translation is never revealed (translate-it).
 * - `harder` / `easier` — move the game's difficulty one notch, from now on
 *              (translate-it, guess-it, three-words, time-machine; not
 *              describe-it, whose words the app deals).
 */
export const GAME_ACTIONS = ['retry', 'skip', 'harder', 'easier', 'reveal'] as const;
export const gameActionSchema = z.enum(GAME_ACTIONS);
/**
 * - `reveal` — «No sé» on the challenge on the table (translate-it, plan 47
 *              Round 2): the server generates its solution and sends it in a
 *              `game.solution` message (`source: 'reveal'`), and the coach
 *              serves a FRESH challenge without ever saying the translation.
 *              The revealed challenge counts as «vista» ({@link gameScoreOf}).
 */
export type GameAction = z.infer<typeof gameActionSchema>;

/**
 * The client's id of one «No sé» / «Ver solución» request (plan 47 Round 2
 * review, 2026-10-17): the server echoes it in the `game.solution` that
 * answers it, so the app pairs each answer with ITS request — not by arrival
 * order (two reveals in flight can answer out of order) nor by position in a
 * list that a reconnect resets. Unique per app session (any opaque string).
 */
export const gameSolutionRequestIdSchema = z.string().min(1).max(64);

/**
 * «Ver solución» on an attempt the learner already made (plan 47 Round 2,
 * translate-it): the server generates the solution of the challenge THAT
 * attempt answered and sends it in a `game.solution` message
 * (`source: 'solution'`); no coach turn. `turnIndex` = the attempt bubble's
 * `gameAttempt` anchor (the `turnIndex` of its `game.round` verdict). Only an
 * attempt the session judged (`correct` / `minor` / `wrong`) gets one: the
 * server refuses anything else, so it never becomes a free translator.
 */
export const gameSolutionRequestSchema = z
  .object({
    action: z.literal('solution'),
    turnIndex: z.number().int().nonnegative(),
    /** Echoed in its `game.solution` answer (see {@link gameSolutionRequestIdSchema}). */
    requestId: gameSolutionRequestIdSchema.optional(),
    /**
     * The session that judged the attempt (2026-10-18). A reconnect that
     * lands on a NEW session row (a resume) restarts the turn numbering, so
     * an attempt bubble from before it names its turn in the PREVIOUS
     * session's numbering. Absent = the current session. The server accepts
     * only the current session or the one the history was resumed from.
     */
    sessionId: z.string().uuid().optional(),
  })
  .strict();
export type GameSolutionRequest = z.infer<typeof gameSolutionRequestSchema>;

/**
 * «No sé» carrying its request id (`{ action: 'reveal', requestId }`), the form
 * the app sends since the Round 2 review. The plain `'reveal'` action stays
 * valid (its answer echoes `requestId: null`).
 */
export const gameRevealRequestSchema = z
  .object({
    action: z.literal('reveal'),
    requestId: gameSolutionRequestIdSchema,
  })
  .strict();
export type GameRevealRequest = z.infer<typeof gameRevealRequestSchema>;

/**
 * What a `session.update { gameAction }` carries: a plain action
 * ({@link GAME_ACTIONS}), a «No sé» with its request id, or a «Ver solución»
 * request. Validated at the server's edge with this schema.
 */
export const gameActionRequestSchema = z.union([
  gameActionSchema,
  gameRevealRequestSchema,
  gameSolutionRequestSchema,
]);
export type GameActionRequest = z.infer<typeof gameActionRequestSchema>;

/** The `session.update` patch key of a game action: `{ gameAction: 'retry' }`. */
export const GAME_ACTION_PATCH_KEY = 'gameAction';

/** The `session.update` patch of a game action (or a «Ver solución» request). */
export function gameActionPatch(action: GameActionRequest): { gameAction: GameActionRequest } {
  return { [GAME_ACTION_PATCH_KEY]: action };
}

/* -------------------------------------------------------------------------- */
/* Challenge band (plan 47 Round 2)                                           */
/* -------------------------------------------------------------------------- */

/**
 * The level of the challenges, one notch per entry, easiest first: the CEFR
 * levels plus half-steps. Decided in CODE (module-languages
 * `games/bands.ts`): it starts at the learner's CEFR level, moves one notch
 * down after 2 `wrong` first tries in a row (a «No sé» counts as `wrong`), and
 * otherwise only with the learner's Más fácil / Más difícil — 3 `correct` in a
 * row do not raise it, they make the arena suggest Más difícil (`suggestHarder`).
 */
export const GAME_BANDS = ['A1', 'A2', 'A2+', 'B1', 'B1+', 'B2', 'B2+', 'C1', 'C2'] as const;
export const gameBandSchema = z.enum(GAME_BANDS);
export type GameBand = z.infer<typeof gameBandSchema>;

/**
 * Why the band of a challenge differs from the previous challenge's:
 * `auto-down` (2 `wrong` in a row), `manual` (Más fácil / Más difícil).
 * `null` = same band as before. There is no automatic up (owner's call,
 * 2026-10-21): see `suggestHarder`.
 */
export const GAME_BAND_SHIFTS = ['auto-down', 'manual'] as const;
export const gameBandShiftSchema = z.enum(GAME_BAND_SHIFTS);
export type GameBandShift = z.infer<typeof gameBandShiftSchema>;

/**
 * Where the challenge sits in the coach's reply (plan 47 Round 2): the arena
 * renders it once, as a block INSIDE the coach bubble.
 *
 * - `text`   — the exact substring of the reply that carries the challenge,
 *              markers included (`**Ella trabaja los sábados.**`, `«…»`).
 * - `start` / `end` — UTF-16 offsets of `text` in the reply text: the
 *              concatenation of that reply's `tokens` deltas.
 * - `marker` — how the coach set it apart: `bold` (`text` starts and ends
 *              with `**`), `quotes` (wrapped in «» / "" / “”), `none` (a loose
 *              sentence). The block shows `text` without its markers (the
 *              shared `challenge` is that, normalized).
 *
 * Client rule: if `bubbleText.slice(start, end) === text`, use the offsets;
 * else use the LAST occurrence of `text` in the bubble; else fall back to the
 * normalized `challenge` (render it as a block under the text).
 */
export const gameChallengeSpanSchema = z.object({
  text: z.string().min(1),
  start: z.number().int().nonnegative(),
  end: z.number().int().positive(),
  marker: z.enum(['bold', 'quotes', 'none']),
});
export type GameChallengeSpan = z.infer<typeof gameChallengeSpanSchema>;

/* -------------------------------------------------------------------------- */
/* Round snapshot (WS + history)                                              */
/* -------------------------------------------------------------------------- */

/**
 * The state of the round a `game.round` message (and a history row) talks
 * about. Derived server-side from the conversation itself: nothing here is a
 * counter the client must keep.
 */
export const gameRoundSnapshotSchema = z.object({
  /** Today only `translate-it` emits rounds (the engine game). */
  seed: z.enum(GAME_SEEDS),
  /**
   * The round number (1-based): DISTINCT challenges served in the session so
   * far. A rematch (the same sentence served again) does not add a round.
   * `0` = no challenge served yet.
   */
  round: z.number().int().nonnegative(),
  /** The Spanish challenge sentence; `null` only when none was served yet. */
  challenge: z.string().min(1).nullable(),
  /**
   * Which try at `challenge` this is (1-based): 1 + the learner's earlier
   * attempts at the same sentence in this session that the meaning check
   * JUDGED as tries — a turn with no meaning verdict (check off, failed) or a
   * `not-attempt` does not count. `2` = "2º intento".
   */
  attemptNo: z.number().int().positive(),
  /** Why `challenge` is being tried again; `null` on a first try. */
  rematch: gameRematchSchema.nullable(),
});
export type GameRoundSnapshot = z.infer<typeof gameRoundSnapshotSchema>;

/**
 * The learner turn a `verdict` event judged, anchored like the turn's
 * corrections: `turnIndex` + `userText` are the same anchor a `correction`
 * chip and its `corrections.done` carry (the open turn's index and the exact
 * text the side-runner analyses). `turnIndex` is `null` only when the server
 * had no open turn (degenerate, logged).
 */
export const gameRoundAttemptSchema = z.object({
  turnIndex: z.number().int().nonnegative().nullable(),
  userText: z.string(),
  /**
   * The meaning verdict; `null` = unknown — the check is off for the session
   * (`meaningCheck: false`), timed out or failed. Never stamped: without a
   * meaning verdict nobody can tell an attempt from chat.
   */
  verdict: gameMeaningVerdictSchema.nullable(),
  /**
   * The part of `userText` the meaning check judged: the sentence that says
   * the challenge when the turn has more than one ("I need coffee." of "I
   * need coffee. Is good?"), else the whole attempt (without the scrap the
   * mic cut off). The stamp counts only the chips that touch it
   * ({@link gameJudgedCorrections}); every chip is still shown. `null` = the
   * check did not run for this turn (`verdict` null, or a `not-attempt` the
   * code decided); absent = a server older than this field — both read as
   * "the whole turn".
   */
  judgedText: z.string().min(1).nullable().optional(),
});
export type GameRoundAttempt = z.infer<typeof gameRoundAttemptSchema>;

/**
 * Server → client: the translate-it round state (`type: 'game.round'`).
 *
 * - `event: 'challenge'` — the coach just served a challenge (a fresh one, a
 *   second shot or a retry): `challenge` is non-null, `attempt` is `null`. The
 *   challenge card shows it. Sent when the coach's reply ENDS.
 * - `event: 'verdict'` — the learner's turn was judged, BEFORE the coach's
 *   reply: `attempt` carries the anchor and the meaning verdict; the snapshot
 *   is the round that turn answered. Exactly one per learner turn of the game,
 *   except a turn superseded or cancelled before its check finished (no event:
 *   after the grace period its meaning is unknown — no stamp).
 *
 * `meaningCheck` says whether the session runs the Jev check at all
 * (`translateItAttemptCheckEnabled` + a usable TypeSafe key); with `false`
 * every verdict is `null` (or `not-attempt`), so nothing is ever stamped and
 * the arena keeps only the round (no ✓ / streak).
 */
export const serverGameRoundSchema = gameRoundSnapshotSchema.extend({
  type: z.literal('game.round'),
  event: z.enum(['challenge', 'verdict']),
  meaningCheck: z.boolean(),
  attempt: gameRoundAttemptSchema.nullable(),
  /**
   * The band `challenge` was served at (plan 47 Round 2); `null` = no
   * challenge yet. Absent = a server older than the field.
   */
  band: gameBandSchema.nullable().optional(),
  /**
   * Why `band` differs from the previous challenge's (`null` = it does not,
   * or this is the first challenge). Same on both events of a round, so the
   * «↑ subimos» moment belongs to the `challenge` event that flips the card.
   */
  bandShift: gameBandShiftSchema.nullable().optional(),
  /**
   * `challenge` event only: the learner reached this challenge on a run of
   * `correct` first tries — the arena points them at «Más difícil» (the band
   * never climbs on its own). Absent on `verdict` events and from older servers.
   */
  suggestHarder: z.boolean().optional(),
  /**
   * `challenge` event only: where the challenge sits in the reply that served
   * it ({@link GameChallengeSpan}); `null` when it could not be located
   * verbatim (render the normalized `challenge` instead). Absent on
   * `verdict` events and from older servers.
   */
  challengeSpan: gameChallengeSpanSchema.nullable().optional(),
});
export type ServerGameRoundMessage = z.infer<typeof serverGameRoundSchema>;

/* -------------------------------------------------------------------------- */
/* Solutions («No sé» / «Ver solución»)                                       */
/* -------------------------------------------------------------------------- */

/** What asked for a solution: `reveal` («No sé») or `solution` («Ver solución»). */
export const GAME_SOLUTION_SOURCES = ['reveal', 'solution'] as const;
export const gameSolutionSourceSchema = z.enum(GAME_SOLUTION_SOURCES);
export type GameSolutionSource = z.infer<typeof gameSolutionSourceSchema>;

/**
 * Why a solution request got no solution:
 *
 * - `refused`           — nothing to solve: no translate-it challenge on the
 *                         table (reveal), or `turnIndex` is not an attempt the
 *                         session judged (solution). No coach turn either.
 * - `generation_failed` — the model call failed (timeout, provider error,
 *                         malformed answer). On a reveal the coach still moved
 *                         on: the challenge counts as «vista».
 */
export const GAME_SOLUTION_FAILURES = ['refused', 'generation_failed'] as const;
export type GameSolutionFailure = (typeof GAME_SOLUTION_FAILURES)[number];

/**
 * Server → client: the answer to a `reveal` or `solution` game action
 * (`type: 'game.solution'`, plan 47 Round 2). Exactly one per request.
 *
 * - `status: 'ready'`   — `solutions` has 1–2 natural English versions at the
 *   challenge's band: the most natural first; with `source: 'solution'` and a
 *   learner attempt, the second (when present) is the one closest to their
 *   own wording. The UI shows them; the coach never says them.
 * - `status: 'failed'`  — `reason` says why (`solutions` empty); the UI says
 *   «No pudimos traer la solución».
 *
 * `turnIndex`: `solution` → the attempt's turn (the request's); `reveal` →
 * `null`. `challenge` / `round` / `band`: the challenge solved (`null` /
 * `0` / `null` only on a `refused` with nothing on the table).
 */
export const serverGameSolutionSchema = z
  .object({
    type: z.literal('game.solution'),
    source: gameSolutionSourceSchema,
    status: z.enum(['ready', 'failed']),
    reason: z.enum(GAME_SOLUTION_FAILURES).nullable(),
    seed: z.enum(GAME_SEEDS),
    round: z.number().int().nonnegative(),
    challenge: z.string().min(1).nullable(),
    band: gameBandSchema.nullable(),
    turnIndex: z.number().int().nonnegative().nullable(),
    solutions: z.array(z.string().min(1)).max(2),
    /**
     * The `requestId` of the request this answers; `null` = the request
     * carried none (a plain `'reveal'`); absent = a server older than the field.
     */
    requestId: gameSolutionRequestIdSchema.nullable().optional(),
  })
  .superRefine((m, ctx) => {
    const ok =
      m.status === 'ready'
        ? m.reason === null && m.solutions.length >= 1 && m.challenge !== null
        : m.reason !== null && m.solutions.length === 0;
    if (!ok) {
      ctx.addIssue({
        code: 'custom',
        message: 'game.solution: ready ⇔ 1–2 solutions, a challenge and no reason',
      });
    }
  });
export type ServerGameSolutionMessage = z.infer<typeof serverGameSolutionSchema>;

/**
 * A solution as the session transcript returns it
 * (`GET …/sessions/:id/transcript`, `data.gameSolutions`, table
 * `language_game_solutions`), in the order they were asked for. `solution`
 * rows belong to the attempt at `attemptTurnIndex`; `reveal` rows to the
 * round, placed after the turn `afterTurnIndex` (the last turn when the
 * learner said «No sé»; `null` = before any). `solutions` empty = the
 * generation failed (a reveal still counts as «vista»).
 */
export const transcriptGameSolutionSchema = z.object({
  source: gameSolutionSourceSchema,
  seed: z.enum(GAME_SEEDS),
  round: z.number().int().nonnegative(),
  challenge: z.string().min(1),
  band: gameBandSchema.nullable(),
  attemptTurnIndex: z.number().int().nonnegative().nullable(),
  afterTurnIndex: z.number().int().nonnegative().nullable(),
  solutions: z.array(z.string().min(1)).max(2),
});
export type TranscriptGameSolution = z.infer<typeof transcriptGameSolutionSchema>;

/**
 * One learner turn's round as the session transcript returns it
 * (`GET /api/modules/english/sessions/:id/transcript`, `turns[].gameRound`,
 * table `language_game_rounds`): the snapshot plus the persisted meaning
 * verdict. Feed it to {@link gameTurnStampOf} with the turn's `userText`,
 * its `corrections` and its `correctionsVerdict`.
 */
export const transcriptGameRoundSchema = gameRoundSnapshotSchema.extend({
  meaningVerdict: gameMeaningVerdictSchema.nullable(),
  /** The sentence the check judged (`game.round` `attempt.judgedText`); `null` = the whole turn. */
  judgedText: z.string().min(1).nullable(),
  /** The band the challenge was served at (plan 47 Round 2); `null` = rows before migration 0422. */
  band: gameBandSchema.nullable(),
});
export type TranscriptGameRound = z.infer<typeof transcriptGameRoundSchema>;

/* -------------------------------------------------------------------------- */
/* The stamp                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * The stamp of a judged translate-it attempt:
 *
 * - `perfect` — «¡Perfecto!»
 * - `good`    — «¡Bien!» (the minor chip stays)
 * - `close`   — «Casi»
 * - `miss`    — «No era eso»
 */
export const GAME_STAMPS = ['perfect', 'good', 'close', 'miss'] as const;
export type GameStamp = (typeof GAME_STAMPS)[number];

/**
 * What the stamp reads of a correction: its severity (and kind, to skip
 * phonetics) — and, to place it in the turn, the words it quotes.
 */
export interface GameStampCorrection {
  /**
   * Same scale as the `correction` chip (`communication-breaking` |
   * `structural` | `minor` | `suggestion`). `null` / absent — or any other
   * string, e.g. a history row from a newer scale — counts as `structural`,
   * as everywhere else (fail-safe: show it as an error; same rule as
   * `skill-praise.ts`). Typed `string` so the transcript's rows pass as is.
   */
  severity?: string | null | undefined;
  /**
   * Pronunciation corrections never change the stamp: the history's
   * transcript mixes both kinds in one list, so `phonetic` ones are skipped.
   * Absent = grammar (the live bubble keeps pronunciation apart).
   */
  kind?: 'grammar' | 'phonetic' | undefined;
  /**
   * The learner's words the correction quotes (the chip's `said`), to tell
   * whether it touches the sentence the meaning check judged
   * ({@link gameJudgedCorrections}). Absent / not found in the turn = it
   * counts (fail-safe: an error nobody can place is not ignored).
   */
  said?: string | null | undefined;
}

/**
 * The corrections verdict as the stamp reads it: the turn's
 * `corrections.done` status (the live `CorrectionsVerdict['status']`, or the
 * transcript's `correctionsVerdict`), or `null` = it did not arrive (yet, or
 * within the grace period).
 */
export type GameStampCorrectionsStatus = CorrectionsVerdict['status'];

/**
 * How long a live bubble waits for its inputs (the meaning verdict and the
 * corrections verdict) before it stamps with what it has. The corrector ends
 * ~1.3 s after the reply starts, which starts after the check (≤ 0.8 s): 6 s
 * from the learner's turn covers the slow tail without leaving a bubble
 * "judging" forever. Client-side constant; the server never waits.
 */
export const GAME_STAMP_GRACE_MS = 6000;

/**
 * The stamp of one attempt (plan 47 § The stamp). Pure; the order of the rules
 * is the table's:
 *
 * | input | stamp |
 * |---|---|
 * | meaning `not-attempt`, or unknown (`null`) | `null` (no stamp) |
 * | meaning `wrong`, or any grammar correction `communication-breaking` | `miss` |
 * | any grammar correction `structural` (or with no severity) | `close` |
 * | corrector `ok` with only `minor` / `suggestion` corrections | `good` |
 * | corrector `ok` with no correction | `perfect` |
 * | corrector `failed` / `skipped` / not arrived | meaning only: `correct` → `perfect`, `minor` → `close` |
 *
 * Meaning unknown = no stamp (plan 47 review): with no meaning verdict (the
 * session has no check, it failed or never answered) the turn may be chat —
 * «what does sábados mean?» — and stamping it from the form would celebrate,
 * score and count a question. Here and not in each caller: the live thread
 * and the history must apply the same rule. It never waits: while the inputs
 * are pending the CALLER shows "judging" (see {@link GAME_STAMP_GRACE_MS}) and
 * passes `null` for what did not arrive once the grace is over.
 */
export function gameStampOf(
  meaning: GameMeaningVerdict | null,
  corrections: ReadonlyArray<GameStampCorrection>,
  correctionsVerdict: GameStampCorrectionsStatus | null,
): GameStamp | null {
  if (meaning === null || meaning === 'not-attempt') return null;
  const grammar = corrections.filter((c) => c.kind !== 'phonetic');
  const severityOf = (c: GameStampCorrection): 'communication-breaking' | 'structural' | 'light' =>
    c.severity === 'communication-breaking'
      ? 'communication-breaking'
      : c.severity === 'minor' || c.severity === 'suggestion'
        ? 'light'
        : 'structural';
  if (meaning === 'wrong' || grammar.some((c) => severityOf(c) === 'communication-breaking')) {
    return 'miss';
  }
  if (grammar.some((c) => severityOf(c) === 'structural')) return 'close';
  if (correctionsVerdict === 'ok') return grammar.length > 0 ? 'good' : 'perfect';
  return meaning === 'correct' ? 'perfect' : 'close';
}

/**
 * The corrections of a turn that touch the sentence the meaning check judged
 * (plan 47, `attempt.judgedText`): only those may stamp the attempt. A learner
 * often says something after the translation ("I need coffee. Is good?"), and
 * a chip on that ("Is good?" → "Is it good?") stamped a clean translation
 * «Casi» (~10 % of clean attempts, plan 47 Phase 3). Every chip is still SHOWN
 * on the bubble; this only decides which ones the stamp and the summary's
 * corrected form read.
 *
 * Placement is the bubble underline's (`locateTextSpans`, text-span.ts): each
 * correction's `said` is located in `turnText` the way the bubble underlines
 * it (a repeated quote lands on the next occurrence), the judged sentence is
 * located the same way (its first occurrence — the check keeps the first of
 * two equal sentences), and a correction counts when its span overlaps the
 * sentence's. So the stamp reads a chip exactly where the learner sees it
 * underlined.
 *
 * Unchanged list (every correction counts) when there is nothing to filter
 * by: `judgedText` null / absent / blank, or not found in `turnText`. A
 * correction whose `said` is absent or not found counts too (an error nobody
 * can place is not ignored). `turnText` must be the text the corrections were
 * made on: the live bubble's `text`, the transcript's `userText`.
 */
export function gameJudgedCorrections<C extends GameStampCorrection>(
  turnText: string,
  judgedText: string | null | undefined,
  corrections: ReadonlyArray<C>,
): C[] {
  if (!judgedText || judgedText.trim() === '') return [...corrections];
  const judged = locateTextSpan(turnText, judgedText.trim());
  if (!judged) return [...corrections];
  const spans = locateTextSpans(
    turnText,
    corrections.map((c) => c.said ?? ''),
  );
  return corrections.filter((_, i) => {
    const span = spans[i];
    if (!span) return true;
    return span.start < judged.end && span.end > judged.start;
  });
}

/** The inputs of one attempt's stamp, as every caller has them. */
export interface GameTurnStampInput {
  meaning: GameMeaningVerdict | null;
  /** The text the corrections were made on (live bubble `text`, transcript `userText`). */
  turnText: string;
  /** `attempt.judgedText` / the transcript's `gameRound.judgedText`; `null` / absent = the whole turn. */
  judgedText: string | null | undefined;
  corrections: ReadonlyArray<GameStampCorrection>;
  correctionsVerdict: GameStampCorrectionsStatus | null;
}

/**
 * The stamp of one attempt from what the live thread, the history and the
 * eval harness all have: {@link gameStampOf} on the corrections that touch
 * the judged sentence ({@link gameJudgedCorrections}). The one call every
 * caller makes, so the three can never stamp the same turn differently.
 */
export function gameTurnStampOf(input: GameTurnStampInput): GameStamp | null {
  return gameStampOf(
    input.meaning,
    gameJudgedCorrections(input.turnText, input.judgedText, input.corrections),
    input.correctionsVerdict,
  );
}

/** A stamp the arena celebrates (sound, haptic, streak). */
export function isPositiveGameStamp(stamp: GameStamp | null): boolean {
  return stamp === 'perfect' || stamp === 'good';
}

/** Streak lengths that play `game-streak` (plan 47 § The stamp). */
export const GAME_STREAK_MILESTONES: readonly number[] = [3, 5, 10];

/**
 * One entry of a game's run, in order: an attempt's stamp, `'seen'` = a
 * challenge the learner revealed with «No sé» (plan 47 Round 2), or `null` =
 * not an attempt (skipped: it neither counts nor breaks a streak).
 */
export const GAME_SEEN = 'seen';
export type GameScoreEntry = GameStamp | typeof GAME_SEEN | null;

/** The score of a game, from its attempts' stamps in order. */
export interface GameScore {
  /** Stamped attempts (`null` stamps — not attempts — and «vista» are not counted). */
  attempts: number;
  /** `perfect` + `good`: the ✓ count of the score pill. */
  clean: number;
  close: number;
  miss: number;
  /**
   * Challenges revealed with «No sé» («vista»): neither a win nor a miss,
   * and not an attempt — but the challenge was not answered, so it ENDS the
   * current streak (decision 2026-10-17: a streak counts challenges answered
   * cleanly in a row, and a revealed one interrupts the row).
   */
  seen: number;
  /** Positive stamps in a row at the end; a `close`, a `miss` or a «vista» breaks it. */
  streak: number;
  bestStreak: number;
}

/**
 * The score of a game from its entries in order ({@link GameScoreEntry}:
 * stamps, «vista», `null` = not an attempt, skipped). Pure — the live pill,
 * the summary card and the history compute it the same way.
 */
export function gameScoreOf(entries: ReadonlyArray<GameScoreEntry>): GameScore {
  const score: GameScore = {
    attempts: 0,
    clean: 0,
    close: 0,
    miss: 0,
    seen: 0,
    streak: 0,
    bestStreak: 0,
  };
  for (const entry of entries) {
    if (entry === null) continue;
    if (entry === GAME_SEEN) {
      score.seen += 1;
      score.streak = 0;
      continue;
    }
    score.attempts += 1;
    if (isPositiveGameStamp(entry)) {
      score.clean += 1;
      score.streak += 1;
      score.bestStreak = Math.max(score.bestStreak, score.streak);
      continue;
    }
    if (entry === 'close') score.close += 1;
    else score.miss += 1;
    score.streak = 0;
  }
  return score;
}

/** `true` when `streak` just reached one of {@link GAME_STREAK_MILESTONES}. */
export function isGameStreakMilestone(streak: number): boolean {
  return GAME_STREAK_MILESTONES.includes(streak);
}
